import React, { useEffect, useMemo, useRef, useState } from "react";
import i18n from "i18next";
import { Button, Empty, Input, List, Space, Spin, Switch, Tag, Typography, Alert } from "antd";
import { SearchOutlined, ReloadOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";

const { Text } = Typography;

function bindingValue(binding, name) {
  return binding?.[name]?.value || "";
}

function bindingLang(binding, name) {
  return binding?.[name]?.["xml:lang"] || binding?.[name]?.xmlLang || "";
}

function normalizeText(value) {
  return String(value || "").toLowerCase().trim();
}

function getMainLabel(labels) {
  if (!labels || labels.length === 0) return "";

  const preferredRu = labels.find((item) => item.predicate.endsWith("prefLabel") && item.lang === "ru");
  if (preferredRu) return preferredRu.value;

  const preferredAny = labels.find((item) => item.predicate.endsWith("prefLabel"));
  if (preferredAny) return preferredAny.value;

  const firstRu = labels.find((item) => item.lang === "ru");
  if (firstRu) return firstRu.value;

  return labels[0].value;
}

function normalizeConceptUris(result) {
  if (!Array.isArray(result)) return [];

  return Array.from(
    new Set(
      result
        .map((item) => {
          if (typeof item === "string") return item;
          if (item && typeof item === "object") {
            return item?.concept?.value || item?.uri?.value || item.uri || item.value || item.concept || "";
          }
          return "";
        })
        .filter(Boolean)
    )
  );
}

function labelsResponseToLabelsByUri(data) {
  const nextLabelsByUri = {};

  const bindings = Array.isArray(data)
    ? data
    : data?.results?.bindings || [];

  for (const binding of bindings) {
    const uri = bindingValue(binding, "uri") || bindingValue(binding, "concept");
    const label = bindingValue(binding, "label");
    const lang = bindingLang(binding, "label");
    const predicate = bindingValue(binding, "labelPredicate") || bindingValue(binding, "predicate") || "label";

    if (!uri || !label) continue;

    if (!nextLabelsByUri[uri]) nextLabelsByUri[uri] = [];
    nextLabelsByUri[uri].push({ value: label, lang, predicate });
  }

  return nextLabelsByUri;
}

async function runMaybeThunk(dispatch, loader, ...args) {
  if (typeof loader !== "function") {
    throw new Error(i18n.t("components.skos.selectConcept.loaderMissing"));
  }

  const result = loader(...args);

  if (typeof result === "function") {
    return dispatch(result);
  }

  return result;
}

export default function SelectConcept({
  onSelect,
  loadConcepts,
  loadNotInSchemeConcepts = null,
  loadLabels,
  autoLoad = true,
  pageSize = 100,
  searchLabelLoadLimit = 500,
}) {
  const dispatch = useDispatch();
  const hasNotInSchemeLoader = typeof loadNotInSchemeConcepts === "function";

  const [loadingUris, setLoadingUris] = useState(false);
  const [loadingLabels, setLoadingLabels] = useState(false);
  const [error, setError] = useState("");
  const [uris, setUris] = useState([]);
  const [labelsByUri, setLabelsByUri] = useState({});
  const [search, setSearch] = useState("");
  const [showAllConcepts, setShowAllConcepts] = useState(!hasNotInSchemeLoader);

  const loadedLabelUrisRef = useRef(new Set());

  const loadUris = async (allConcepts = showAllConcepts) => {
    setError("");
    setLoadingUris(true);
    setUris([]);
    setLabelsByUri({});
    loadedLabelUrisRef.current = new Set();

    try {
      const loader = allConcepts || !hasNotInSchemeLoader
        ? loadConcepts
        : loadNotInSchemeConcepts;

      const result = await runMaybeThunk(dispatch, loader);
      setUris(normalizeConceptUris(result));
    } catch (e) {
      setError(e.message || i18n.t("components.skos.selectConcept.loadConceptsError"));
    } finally {
      setLoadingUris(false);
    }
  };

  const loadLabelsForUris = async (targetUris) => {
    if (typeof loadLabels !== "function") {
      return;
    }

    const notLoaded = targetUris.filter((uri) => !loadedLabelUrisRef.current.has(uri));
    if (notLoaded.length === 0) return;

    notLoaded.forEach((uri) => loadedLabelUrisRef.current.add(uri));

    setLoadingLabels(true);
    setError("");

    try {
      const data = await runMaybeThunk(dispatch, loadLabels, notLoaded);
      const nextLabelsByUri = labelsResponseToLabelsByUri(data);

      setLabelsByUri((prev) => {
        const merged = { ...prev };

        for (const uri of notLoaded) {
          const oldLabels = merged[uri] || [];
          const newLabels = nextLabelsByUri[uri] || [];
          const seen = new Set(oldLabels.map((item) => `${item.predicate}|${item.lang}|${item.value}`));

          merged[uri] = [...oldLabels];
          for (const item of newLabels) {
            const key = `${item.predicate}|${item.lang}|${item.value}`;
            if (!seen.has(key)) {
              seen.add(key);
              merged[uri].push(item);
            }
          }
        }

        return merged;
      });
    } catch (e) {
      for (const uri of notLoaded) loadedLabelUrisRef.current.delete(uri);
      setError(e.message || i18n.t("components.skos.selectConcept.loadLabelsError"));
    } finally {
      setLoadingLabels(false);
    }
  };

  useEffect(() => {
    if (!hasNotInSchemeLoader && !showAllConcepts) {
      setShowAllConcepts(true);
    }
  }, [hasNotInSchemeLoader, showAllConcepts]);

  useEffect(() => {
    if (autoLoad) loadUris(showAllConcepts);
  }, [autoLoad, showAllConcepts, loadConcepts, loadNotInSchemeConcepts]);

  const filteredUris = useMemo(() => {
    const q = normalizeText(search);
    if (!q) return uris;

    return uris.filter((uri) => {
      const uriMatched = normalizeText(uri).includes(q);
      if (uriMatched) return true;

      const labels = labelsByUri[uri] || [];
      return labels.some((label) => normalizeText(label.value).includes(q));
    });
  }, [uris, labelsByUri, search]);

  const visibleUris = useMemo(() => filteredUris.slice(0, pageSize), [filteredUris, pageSize]);

  useEffect(() => {
    if (visibleUris.length === 0) return;
    loadLabelsForUris(visibleUris);
  }, [visibleUris.join("|"), loadLabels]);

  const handleSearchChange = async (e) => {
    const value = e.target.value;
    setSearch(value);

    if (value.trim()) {
      await loadLabelsForUris(uris.slice(0, searchLabelLoadLimit));
    }
  };

  const handleShowAllConceptsChange = (checked) => {
    setShowAllConcepts(checked);
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Space.Compact style={{ width: "100%" }}>
        <Input
          allowClear
          value={search}
          prefix={<SearchOutlined />}
          placeholder={i18n.t("components.skos.selectConcept.searchPlaceholder")}
          onChange={handleSearchChange}
        />
        <Button icon={<ReloadOutlined />} onClick={() => loadUris(showAllConcepts)} loading={loadingUris}>
          {i18n.t("components.skos.selectConcept.reload")}
        </Button>
      </Space.Compact>

      <Space style={{ justifyContent: "space-between", width: "100%" }}>
        <Text type="secondary">
          {i18n.t("components.skos.selectConcept.found", { filtered: filteredUris.length, total: uris.length })}
        </Text>

        {hasNotInSchemeLoader && (
          <Space>
            <Text>{i18n.t(showAllConcepts
              ? "components.skos.selectConcept.allConcepts"
              : "components.skos.selectConcept.unlinkedConcepts")}</Text>
            <Switch checked={showAllConcepts} onChange={handleShowAllConceptsChange} />
          </Space>
        )}
      </Space>

      {error && <Alert type="error" showIcon message={i18n.t("components.skos.selectConcept.error")} description={error} />}

      <Spin spinning={loadingUris || loadingLabels} tip={i18n.t(loadingUris
        ? "components.skos.selectConcept.loadingUris"
        : "components.skos.selectConcept.loadingLabels")}>
        {filteredUris.length === 0 ? (
          <Empty description={i18n.t("components.skos.selectConcept.notFound")} />
        ) : (
          <List
            bordered
            dataSource={visibleUris}
            style={{ maxHeight: 560, overflowY: "auto" }}
            renderItem={(uri) => {
              const labels = labelsByUri[uri] || [];
              const mainLabel = getMainLabel(labels);
              const labelsToShow = labels.slice(0, 1);

              return (
                <List.Item
                  actions={[
                    <Button key="select" type="link" onClick={() => onSelect?.({ uri, labels })}>
                      {i18n.t("components.skos.selectConcept.select")}
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <Space direction="vertical" size={2} style={{ width: "100%" }}>
                        <Text strong>{mainLabel || i18n.t("components.skos.selectConcept.noLabel")}</Text>
                        <Text copyable style={{ wordBreak: "break-all" }} type="secondary">
                          {uri}
                        </Text>
                      </Space>
                    }
                    description={
                      labelsToShow.length > 0 ? (
                        <Space direction="vertical" size={4}>
                          {labelsToShow.map((label, index) => (
                            <Space key={`${label.predicate}-${label.lang}-${label.value}-${index}`} wrap>
                              <Tag>{label.predicate.split("#").pop().split("/").pop()}</Tag>
                              {label.lang && <Tag>{label.lang}</Tag>}
                              <Text>{label.value}</Text>
                            </Space>
                          ))}
                        </Space>
                      ) : (
                        <Text type="secondary">{i18n.t("components.skos.selectConcept.labelsNotFound")}</Text>
                      )
                    }
                  />
                </List.Item>
              );
            }}
          />
        )}
      </Spin>

      {filteredUris.length > visibleUris.length && (
        <Alert
          type="info"
          showIcon
          message={i18n.t("components.skos.selectConcept.firstItemsShown", { count: visibleUris.length })}
        />
      )}
    </Space>
  );
}
